"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AnchorsComponent = void 0;
const Point2D_1 = require("../../Core/Point2D");
const IRegionCallbacks_1 = require("../../Interface/IRegionCallbacks");
const RegionComponent_1 = require("./RegionComponent");
/**
 * An abstract visual component used internall to draw anchor points that allow
 * region points moving and this component resizing.
 */
class AnchorsComponent extends RegionComponent_1.RegionComponent {
    /**
     * Creates a new `AnchorsComponent` object.
     * @param paper - The `Snap.Paper` object to draw on.
     * @param paperRect - The parent bounding box for created component.
     * @param regionData - The `RegionData` object shared across components. Used also for initial setup.
     * @param callbacks - The external callbacks collection.
     */
    constructor(paper, paperRect = null, regionData, callbacks) {
        super(paper, paperRect, regionData, callbacks);
        this.mixins = [];
        /**
         * The index of currently active anchor.
         * 0 - none
         * 1, 2, ... - one of point anchors
         * -1, -2, ... - one of bone anchors
         */
        this.activeAnchorIndex = 0;
        /**
         * Dragging state of the component.
         */
        this.isDragged = false;
        this.node = this.paper.g();
        this.node.addClass("anchorsLayer");
        this.anchors = [];
        this.anchorsNode = this.paper.g();
        this.node.add(this.anchorsNode);
        this.buildAnchors();
    }
    /**
     * Redraws the visual on the component.
     */
    redraw() {
        if (this.regionData.points !== null && this.regionData.points.length > 0) {
            window.requestAnimationFrame(() => {
                this.regionData.points.forEach((p, index) => {
                    this.anchors[index].attr({
                        cx: p.x,
                        cy: p.y,
                    });
                });
            });
        }
        this.mixins.forEach(m => m.redraw());
    }
    /**
     * Switches the component to the frozen state.
     */
    freeze() {
        super.freeze();
    }
    /**
     * Creates a collection on anchors.
     */
    buildAnchors() {
        this.buildPointAnchors();
        this.buildGhostAnchor();
        this.subscribeToEvents([
            {
                event: "pointerleave",
                base: this.node.node,
                listener: (e) => {
                    if (!this.isDragged) {
                        window.requestAnimationFrame(() => {
                            this.ghostAnchor.attr({
                                display: "none",
                            });
                        });
                    }
                },
                bypass: true,
            },
        ]);
        this.mixins.forEach(m => m.buildAnchors());
    }
    /**
     * Creates a collection of anchor points.
     */
    buildPointAnchors() {
        this.regionData.points.forEach((point, index) => {
            const anchor = this.createAnchor(this.paper, point.x, point.y);
            this.anchors.push(anchor);
            this.anchorsNode.add(anchor);
            this.subscribeAnchorToEvents(anchor, index + 1);
        });
    }
    /**
     * Creates the ghost anchor.
     */
    buildGhostAnchor() {
        this.ghostAnchor = this.createAnchor(this.paper, 0, 0, "ghost", AnchorsComponent.DEFAULT_GHOST_ANCHOR_RADIUS);
        this.ghostAnchor.attr({
            display: "none",
        });
        this.node.add(this.ghostAnchor);
        this.subscribeGhostToEvents();
    }
    /**
     * Helper function to subscribe anchor to activation event.
     * @param anchor - The anchor for wire up.
     * @param index - The index of the anchor used to define which one is active.
     */
    subscribeAnchorToEvents(anchor, index) {
        this.subscribeToEvents([
            {
                event: "pointerenter",
                base: anchor.node,
                listener: (e) => {
                    // Set drag origin point to current anchor
                    this.activeAnchorIndex = index;
                    const anchorPoint = this.getActiveAnchorPoint(e);
                    // Move ghost anchor to current anchor position
                    window.requestAnimationFrame(() => {
                        this.ghostAnchor.attr({
                            cx: anchorPoint.x,
                            cy: anchorPoint.y,
                            display: "block",
                        });
                    });
                },
                bypass: false,
            },
        ]);
    }
    /**
     * Helper function to create a new anchor.
     * @param paper - The `Snap.Paper` object to draw on.
     * @param x - The `x`-coordinate of the acnhor.
     * @param y - The `y`-coordinate of the anchor.
     * @param style - Additional css style class to be applied.
     * @param r - The radius of the anchor.
     */
    createAnchor(paper, x, y, style, r = AnchorsComponent.DEFAULT_ANCHOR_RADIUS) {
        const a = paper.circle(x, y, r);
        a.addClass("anchorStyle");
        if (style !== undefined && style !== "") {
            a.addClass(style);
        }
        return a;
    }
    /**
     * Callback for the pointerenter event for the ghost anchor.
     * @param e - PointerEvent object.
     */
    onGhostPointerEnter(e) {
        // do nothing
    }
    /**
     * Callback for the pointerleave event for the ghost anchor.
     * @param e - PointerEvent object.
     */
    onGhostPointerLeave(e) {
        if (!this.isDragged) {
            window.requestAnimationFrame(() => {
                this.ghostAnchor.attr({
                    display: "none",
                });
            });
            this.activeAnchorIndex = 0;
        }
    }
    /**
     * Callback for the pointerdown event for the ghost anchor.
     * @param e - PointerEvent object.
     */
    onGhostPointerDown(e) {
        this.ghostAnchor.node.setPointerCapture(e.pointerId);
        const offsetX = e.clientX - e.target.closest("svg").getBoundingClientRect().left;
        const offsetY = e.clientY - e.target.closest("svg").getBoundingClientRect().top;
        this.dragOrigin = new Point2D_1.Point2D(offsetX, offsetY);
        this.isDragged = true;
        this.callbacks.onManipulationLockRequest(this);
        this.callbacks.onChange(this, this.regionData.copy(), IRegionCallbacks_1.ChangeEventType.MOVEBEGIN);
    }
    /**
     * Callback for the pointermove event for the ghost anchor.
     * @param e - PointerEvent object.
     */
    onGhostPointerMove(e) {
        if (this.isDragged) {
            const ghost = e.target.getBoundingClientRect();
            const rdx = e.clientX - ghost.left;
            const rdy = e.clientY - ghost.top;
            const offsetX = e.clientX - e.target.closest("svg").getBoundingClientRect().left;
            const offsetY = e.clientY - e.target.closest("svg").getBoundingClientRect().top;
            let dx = offsetX - this.dragOrigin.x;
            let dy = offsetY - this.dragOrigin.y;
            if ((rdx < 0 && dx > 0) || (rdx > 0 && dx < 0)) {
                dx = 0;
            }
            if ((rdy < 0 && dy > 0) || (rdy > 0 && dy < 0)) {
                dy = 0;
            }
            if (this.activeAnchorIndex !== 0) {
                const anchorPoint = this.getActiveAnchorPoint(e);
                let p = new Point2D_1.Point2D(anchorPoint.x + dx, anchorPoint.y + dy);
                if (this.paperRect !== null) {
                    p = p.boundToRect(this.paperRect);
                }
                window.requestAnimationFrame(() => {
                    this.ghostAnchor.attr({ cx: p.x, cy: p.y });
                });
                this.updateRegion(p);
            }
            this.dragOrigin = new Point2D_1.Point2D(offsetX, offsetY);
        }
    }
    /**
     * Callback for the pointerup event for the ghost anchor.
     * @param e - PointerEvent object.
     */
    onGhostPointerUp(e) {
        this.ghostAnchor.node.releasePointerCapture(e.pointerId);
        this.callbacks.onManipulationLockRelease(this);
        this.callbacks.onChange(this, this.regionData.copy(), IRegionCallbacks_1.ChangeEventType.MOVEEND);
        this.activeAnchorIndex = 0;
        this.dragOrigin = null;
        this.isDragged = false;
        window.requestAnimationFrame(() => {
            this.ghostAnchor.attr({
                display: "none",
            });
        });
    }
    /**
     * Subscribe event listeners on the ghost anchor
     */
    subscribeGhostToEvents() {
        const listeners = [
            {
                event: "pointerenter",
                base: this.ghostAnchor.node,
                listener: this.onGhostPointerEnter,
                bypass: false,
            },
            {
                event: "pointerleave",
                base: this.ghostAnchor.node,
                listener: this.onGhostPointerLeave,
                bypass: false,
            },
            {
                event: "pointerdown",
                base: this.ghostAnchor.node,
                listener: this.onGhostPointerDown,
                bypass: false,
            },
            {
                event: "pointerup",
                base: this.ghostAnchor.node,
                listener: this.onGhostPointerUp,
                bypass: false,
            },
            {
                event: "pointermove",
                base: this.ghostAnchor.node,
                listener: this.onGhostPointerMove,
                bypass: false,
            },
        ];
        this.subscribeToEvents(listeners);
    }
    /**
     * Returns `Point2D` with coordinates of active anchor
     */
    getActiveAnchorPoint(e) {
        if (this.activeAnchorIndex > 0) {
            return this.regionData.points[this.activeAnchorIndex - 1];
        }
        else {
            return null;
        }
    }
}
exports.AnchorsComponent = AnchorsComponent;
/**
 * Default radius for anchor points. Can be redefined through CSS styles.
 */
AnchorsComponent.DEFAULT_ANCHOR_RADIUS = 3;
/**
 * Default radius for the ghost anchor, used activate dragging. Can be redefined through CSS styles.
 */
AnchorsComponent.DEFAULT_GHOST_ANCHOR_RADIUS = 7;
//# sourceMappingURL=AnchorsComponent.js.map